<template>
    <div
        v-if="invoices"
        class="container-fluid adminGroupInvoicesWrapper">
        <div class="d-block d-md-flex justify-content-between mb-3">
            <div class="d-block d-md-flex flex-wrap align-items-center">
                <div class="mt-2 mr-0 mb-2 mr-md-2 mb-md-0">
                    <div class="input-group input-group-sm">
                        <select
                            v-model="search.status"
                            @change="onChangeSearchStatus"
                            id="status"
                            style="min-width: 100px;"
                            class="form-control">
                            <option :value="null">ステータスで検索</option>
                            <option
                                v-for="sta in invoiceStatus.common"
                                :value="sta.value">
                                {{ sta.text }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="mt-2 mr-0 mb-2 mr-md-2 mb-md-0">
                    <div class="c-freeword input-group input-group-sm">
                        <input
                            v-model="search.freeword"
                            @blur="onChangeSearchFreeword"
                            type="text"
                            class="form-control"
                            placeholder="フリーワード"
                            style="min-width: 100px;">
                        <div class="input-group-append">
                            <div class="c-freeword__btn input-group-text"
                                 @click="onChangeSearchFreeword">検索
                            </div>
                        </div>
                    </div>
                </div>
                <div class="d-block d-md-flex align-items-center">
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <span class="small">請求日</span>
                    </div>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="c-freeword input-group input-group-sm">
                            <input
                                v-model="search.finalized_at_gte"
                                @change="onChangeSearchFinalizedAtGte"
                                type="date"
                                class="form-control"
                                aria-label="Recipient's username"
                                aria-describedby="basic-addon2"
                                style="min-width: 100px;">
                        </div>
                    </div>
                    <span class="small mt-2 ml-2">〜</span>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="c-freeword input-group input-group-sm">
                            <input
                                v-model="search.finalized_at_lte"
                                @change="onChangeSearchFinalizedAtLte"
                                type="date"
                                class="form-control"
                                aria-label="Recipient's username"
                                aria-describedby="basic-addon2"
                                style="min-width: 100px;">
                        </div>
                    </div>
                </div>
            </div>
            <div class="d-flex align-items-center mt-3">
                <div class="d-flex align-items-center">
                    <span style="width: 92px;">ステータスを</span>
                    <select
                        v-model="selectedStatus"
                        class="form-control"
                        style="width: 160px;">
                        <option :value="null">選択してください</option>
                        <option
                            v-for="(status, idx) in filteredInvoiceStatuses"
                            :key="`invoice_status_${idx}`"
                            :value="status.value">
                            {{ status.text }}
                        </option>
                    </select>
                    <span style="padding: 0 5px;">に</span>
                    <button
                        type="button"
                        class="btn btn-yokumiru-orange mr-1"
                        style="min-width: 90px;"
                        :disabled="inProgress || selectedStatus == null || selectedInvoiceIds.length < 1"
                        @click="onClickUpdateInvoiceStatus">
                        {{ inProgress ? '処理中です…' : '変更する' }}
                    </button>
                </div>
                <div class="ml-2">
                    <b-button
                        variant="yokumiru-orange"
                        @click="downloadCSV"
                        :disabled="inProgress">
                        CSVダウンロード
                    </b-button>
                </div>
            </div>
        </div>

        <div class="row justify-content-center">
            <div class="col-md-12">
                <b-table
                    striped
                    hover
                    :items="invoices"
                    :fields="fields"
                    :sort-by.sync="morphState.sortBy"
                    :sort-desc.sync="morphState.sortDesc"
                    @sort-changed="fetchInvoices"
                    :responsive="true"
                    class="table-bordered m-table">
                    <template v-slot:cell(id)="data">
                        {{ data.item.id }}
                    </template>

                    <template v-slot:cell(user_full_name_kana)="data">
                        <template v-if="data.item.user">
                            <a href="#" @click="showModalAdminUserProfile(data.item.user.id)">
                                {{ data.item.user.full_name }} <span class="small">({{data.item.user.age}})</span>
                            </a>
                            <div class="small">{{ data.item.user.full_name_kana }}</div>
                            <span class="text-muted small">#{{ data.item.user_id }}</span>
                        </template>
                        <template v-else>
                            <span class="text-muted">退会済の{{ $locale.patient }}</span>
                        </template>
                    </template>

                    <template v-slot:cell(finalized_at)="data">
                        <span v-if="data.item.finalized_at">
                            {{ helper.dateTimeForHuman(data.item.finalized_at , 'Asia/Tokyo') }}
                        </span>
                    </template>

                    <template v-slot:cell(total)="data">
                        <span>{{ data.item.total.toLocaleString() }}</span><!--
                     --><span class="small">&nbsp;円</span>
                    </template>

                    <template v-slot:cell(content)="data">
                        <div class="d-flex">
                            <div>{{ data.item.content || `医療${$locale.consultation_host}料` }}</div>
                            <template v-if="data.item.consultation">
                                <div class="ml-2">
                                    <span
                                        class="small text-muted">
                                        {{ `${$locale.consultation_host}ID: #${data.item.consultation_id}` }}
                                    </span>
                                </div>
                            </template>
                        </div>
                    </template>

                    <template v-slot:cell(user_coupon)="data">
                        <div v-if="data.item.user_coupon">
                            <span class="u-palt">{{ user_coupon.name }}</span>
                            <span class="u-palt small text-muted">{{ `（発行番号: #${user_coupon.id}）` }}</span>
                        </div>
                    </template>

                    <template v-slot:cell(invoice_attachments)="data">
                        <div
                            v-if="data.item.invoice_attachments.length > 0"
                            class="invoice_attachments d-flex">
                            <a
                                v-for="attachment in data.item.invoice_attachments"
                                :key="`attachment_${attachment.id}`"
                                :href="attachment.presigned_url"
                                target="_blank">
                                <span>{{ attachment.origin_name }}</span>
                            </a>
                        </div>
                    </template>

                    <template v-slot:cell(payment_method_id)="data">
                        <span v-if="data.item.payment_method">
                            {{ data.item.payment_method.name }}
                        </span>
                    </template>

                    <template v-slot:cell(status)="data">
                        <div class="status">
                            <span :class="{ 'is-invalid' : isFailed(data.item.status) }">
                                {{ detectStatusName(data.item) }}
                            </span>
                        </div>
                    </template>

                    <template v-slot:head(invoice_ids)="data">
                        <div class="d-flex align-items-center">
                            <span>全選択</span>
                            <input
                                value="1"
                                class="vc-check-invoice-ids"
                                type="checkbox"
                                :checked="invoices && invoices.length > 0 && selectedInvoiceIds.length === invoices.length"
                                @change="onChangeCheckInvoiceIdsAll">
                        </div>
                    </template>

                    <template v-slot:cell(invoice_ids)="data">
                        <input
                            v-model="selectedInvoiceIds"
                            :value="data.item.id"
                            :id="`invoice_ids${data.item.id}`"
                            class="vc-check-invoice-ids"
                            type="checkbox">
                    </template>

                    <template v-slot:cell(invoice_detail)="data">
                        <div class="d-flex justify-content-end align-items-center">
                            <b-button
                                v-if="viewableInvoice(data.item)"
                                type="button"
                                class="btn btn-sm"
                                variant="yokumiru-blue"
                                target="_blank"
                                :href="data.item.dashboard_url">
                                詳細
                            </b-button>
                        </div>
                    </template>
                </b-table>

                <div class="d-flex flex-wrap justify-content-between align-items-center">
                    <b-pagination
                        :total-rows="morphState.total"
                        :per-page="morphState.per_page"
                        v-model="morphState.current_page"
                        @change="fetchInvoices">
                    </b-pagination>

                    <div class="d-flex flex-wrap align-items-center">
                        <div class="mr-3 mb-3">
                            <span class="small">{{ dispPaginationDetail }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import constants from '../../../constants'
import YokumiruHelper from '../../../YokumiruHelper'
import { sprintf } from 'sprintf-js'
import moment from 'moment-timezone'

export default {
    name: 'AdminGroupInvoices',

    props: {
        group: {
            type: Object,
            required: true,
        },
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            search: {
                status: null,
                freeword: null,
                finalized_at_gte: null,
                finalized_at_lte: null,
            },
            invoices: null,
            selectedInvoiceIds: [],
            selectedStatus: null,
            invoiceStatus: constants.invoices,
            inProgress: false,
            errors: {},
            fields: [
                {
                    key: 'id',
                    label: '#',
                    sortable: true,
                },
                {
                    'key': 'user_full_name_kana',
                    'label': `${this.$locale.patient}名`,
                    sortable: true,
                },
                {
                    key: 'finalized_at',
                    label: '請求日時',
                    sortable: true,
                },
                {
                    'key': 'total',
                    'label': '金額（税込）',
                    sortable: true,
                },
                {
                    key: 'content',
                    label: '請求内容',
                    sortable: false,
                },
                {
                    key: 'payment_method_id',
                    label: 'お支払い方法',
                    sortable: true,
                },
                {
                    key: 'status',
                    label: 'ステータス',
                    sortable: true,
                },
                {
                    key: 'invoice_ids',
                    label: '全選択',
                    sortable: false,
                },
                {
                    key: 'invoice_detail',
                    label: '',
                    class: 'text-center',
                    sortable: false,
                },
            ],
        }
    },

    computed: {
        isCompany: function () {
            return this.helper.isCompany(this.group)
        },

        isOrganization: function () {
            return this.helper.isOrganization(this.group)
        },

        morphState: function () {
            if (this.isCompany) {
                return this.$store.state.adminCompanyInvoices
            }
            else if (this.isOrganization) {
                return this.$store.state.adminOrganizationInvoices
            }
            return null
        },

        /**
         * ページング詳細表示
         * @returns {string}
         */
        dispPaginationDetail: function () {
            if (this.morphState.total < 1) {
                return ''
            }

            return `全${this.morphState.total}件中 ${this.morphState.from}～${this.morphState.to}件表示`
        },

        /**
         * ステータス一括変更で選択可能なステータスのみ返す
         *
         * @return {array}
         */
        filteredInvoiceStatuses: function () {
            const list = [
                this.invoiceStatus.paid,
                this.invoiceStatus.canceled,
            ]
            return this.invoiceStatus.all.filter((status) => list.includes(status.value))
        },
    },

    created: function () {
        if (this.$store.state.configs.subscription == 1) {
            this.fields.splice(6, 0, {
                key: 'user_coupon',
                label: 'クーポン使用',
                sortable: false,
            })
        }
        this.initQueryFromState()
        this.initSearchCondition()
        this.fetchInvoices()
    },

    methods: {
        ...mapMutations({
            setQueryCompany: 'adminCompanyInvoices/setQuery',
            setQueriesCompany: 'adminCompanyInvoices/setQueries',
            setQueryOrganization: 'adminOrganizationInvoices/setQuery',
            setQueriesOrganization: 'adminOrganizationInvoices/setQueries',
        }),

        setQuery: function (data) {
            if (this.isCompany) {
                this.setQueryCompany(data)
            }
            else if (this.isOrganization) {
                this.setQueryOrganization(data)
            }
        },

        setQueries: function (data) {
            if (this.isCompany) {
                this.setQueriesCompany(data)
            }
            else if (this.isOrganization) {
                this.setQueriesOrganization(data)
            }
        },

        /**
         * 検索条件の初期化
         */
        initQueryFromState: function () {
            if (localStorage.getItem('yokumiruWeb')) {
                const data = JSON.parse(localStorage.getItem('yokumiruWeb'))
                if (this.isCompany && data.adminCompanyInvoices) {
                    this.setQueries(data.adminCompanyInvoices)
                }
                else if (this.isOrganization && data.adminOrganizationInvoices) {
                    this.setQueries(data.adminOrganizationInvoices)
                }
            }
        },

        /**
         * 検索条件の初期化
         */
        initSearchCondition: function () {
            this.search.status = this.morphState.status
            this.search.freeword = this.morphState.freeword
            this.search.finalized_at_gte = this.morphState.finalized_at_gte
            this.search.finalized_at_lte = this.morphState.finalized_at_lte
        },

        onChangeSearchStatus: async function () {
            await this.setQuery({ key: 'status', value: this.search.status })
            await this.fetchInvoices(1)
        },

        onChangeSearchFreeword: async function () {
            await this.setQuery({ key: 'freeword', value: this.search.freeword })
            await this.fetchInvoices(1)
        },

        onChangeSearchFinalizedAtGte: async function () {
            await this.setQuery({ key: 'finalized_at_gte', value: this.search.finalized_at_gte })
            await this.fetchInvoices(1)
        },

        onChangeSearchFinalizedAtLte: async function () {
            await this.setQuery({ key: 'finalized_at_lte', value: this.search.finalized_at_lte })
            await this.fetchInvoices(1)
        },

        /**
         * 全選択時
         */
        onChangeCheckInvoiceIdsAll: function () {
            if (!this.invoices || this.invoices.length < 1) return

            if (this.selectedInvoiceIds.length === this.invoices.length) {
                this.selectedInvoiceIds = []
            } else {
                this.selectedInvoiceIds = this.invoices.map((invoice) => invoice.id)
            }
        },

        /**
         * ステータス一括変更時
         */
        onClickUpdateInvoiceStatus: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.admin.invoices.endpoint.base
            const params = {
                invoice_ids: this.selectedInvoiceIds,
                status: this.selectedStatus,
            }

            await axios.put(`${endpoint}/status`, params)
                .then(() => {
                    this.selectedInvoiceIds = []
                    this.fetchInvoices()
                    bus.$emit('alert-success', 'ステータスを変更しました。')
                })
                .catch(({response}) => {
                    if (response &&
                        response.hasOwnProperty('data') &&
                        response.data.hasOwnProperty('errors') &&
                        response.data.errors.hasOwnProperty('message')) {
                        bus.$emit('alert-danger', response.data.errors.message[0])
                    }
                    else {
                        bus.$emit('alert-danger', 'システムエラーが発生しました。')
                    }
                })

            this.inProgress = false
        },

        /**
         * 請求一覧取得
         *
         * @param {number|object} param
         */
        fetchInvoices: async function (param = null) {
            this.invoices = null

            if (Number.isInteger(param) && param > 0) {
                await this.setQuery({ key: 'current_page', value: param })
            }
            else if (param !== null && param.hasOwnProperty('sortBy')) {
                await this.setQuery({ key: 'sortBy', value: param.sortBy })
                await this.setQuery({ key: 'sortDesc', value: param.sortDesc })
            }

            const endpoint = constants.admin.invoices.endpoint.base

            axios.get(`${endpoint}${this.buildQuery()}&group_id=${this.group.id}`)
                .then(({data}) => {
                    this.invoices = data.data
                    this.selectedInvoiceIds = []
                    this.setQueries(data.meta)
                })
        },

        /**
         * QueryString生成
         *
         * @param {boolean} paginate
         * @return {string}
         */
        buildQuery: function (paginate = true) {
            let query = '?'

            const state = this.morphState

            if (paginate) {
                query += `page=${state.current_page}`
                query += `&sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }
            else {
                query += `sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }

            if (state.freeword.length > 0) {
                query += `&freeword=${state.freeword}`
            }
            if (state.status) {
                query += `&status=${state.status}`
            }
            if (state.finalized_at_gte) {
                query += `&finalized_at_gte=${ moment(state.finalized_at_gte).set({'hour' : 0 , 'minute' : 0 , 'second' : 0 }).format( 'YYYY-MM-DD' ) }`;
            }
            if (state.finalized_at_lte) {
                query += `&finalized_at_lte=${ moment(state.finalized_at_lte).set({'hour' : 23 , 'minute' : 59 , 'second' : 59 }).format( 'YYYY-MM-DD' ) }`;
            }

            return query
        },

        /**
         * ステータス表記
         *
         * @param {object} invoice
         * @return {string}
         */
        detectStatusName: function (invoice) {
            let result = this.invoiceStatus.all.find(status => {
                return status.value === invoice.status
            })

            if (result && this.$store.state.configs.subscription != 1) {
                if (result.value === constants.invoices.authorized
                    && invoice.send_at
                    && invoice.payment_method_id !== constants.payment_methods.credit) {
                    return '請求済'
                }
            }

            return result ? result.text : ''
        },

        /**
         * 失敗ステータス判定
         *
         * @param {number} status
         * @return {boolean}
         */
        isFailed: function (status) {
            return status === this.invoiceStatus.failed
                || status === this.invoiceStatus.authorizeFailed
        },

        /**
         * Stripeダッシュボードで閲覧可能な請求書か判定
         *
         * @param {object} invoice
         * @return {boolean}
         */
        viewableInvoice: function (invoice) {
            return invoice.status !== this.invoiceStatus.authorizeFailed
        },

        /**
         * 会員詳細モーダル表示
         *
         * @param {number} userId
         */
        showModalAdminUserProfile: function (userId) {
            bus.$emit('open-modal-admin-user-profile', userId)
        },

        /**
         * CSVダウンロード
         */
        downloadCSV: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = `${constants.admin.invoices.endpoint.base}/export${this.buildQuery(false)}&group_id=${this.group.id}`
            const options = { responseType : 'blob' }

            await axios.get(endpoint, options)
                .then((response) => {
                    const blob = new Blob([response.data], {
                        type: response.data.type,
                    })
                    saveAs(
                        blob,
                        sprintf(
                            '%s_請求一覧_%s.csv',
                            this.group.name,
                            moment().format('YYYYMMDD')
                        )
                    )
                })
                .catch(() => {
                    bus.$emit('alert-danger', 'システムエラーが発生しました。')
                })

            this.inProgress = false
        },
    },
}
</script>

<style lang="scss" scoped>
.vc-check-invoice-ids {
    cursor: pointer;
    width: 15px;
    height: 15px;
    margin-left: 6px;
}
</style>

<style lang="scss">
.adminGroupInvoicesWrapper {
    .b-table {
        min-width: 1200px;

        th, td {
            font-size: 0.75rem;
        }
    }
}
</style>
